'use client';

import Script from 'next/script';
import { Suspense, useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { GA_ID, pageview, track } from '@/lib/analytics';

// Fires a page_view on every client-side route change. Wrapped in <Suspense> by
// the parent because useSearchParams() requires it (and keeps pages static).
function PageViewTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    const query = searchParams.toString();
    pageview(query ? `${pathname}?${query}` : pathname);
  }, [pathname, searchParams]);

  return null;
}

/**
 * Loads GA4 (gtag.js) and wires automatic tracking:
 *  - page_view on route change (initial view fired here too; gtag config skips it)
 *  - cta_click for any element with a [data-analytics-id] attribute (event delegation)
 * Renders nothing and no-ops entirely when NEXT_PUBLIC_GOOGLE_ANALYTICS_ID is unset.
 */
export default function Analytics() {
  // Delegated click tracking  - covers all current and future data-analytics-id CTAs.
  useEffect(() => {
    if (!GA_ID) return;
    const onClick = (e: MouseEvent) => {
      const el = (e.target as HTMLElement | null)?.closest<HTMLElement>('[data-analytics-id]');
      if (el) track('cta_click', { cta_id: el.dataset.analyticsId });
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  if (!GA_ID) return null;

  return (
    <>
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${GA_ID}`}
        strategy="afterInteractive"
      />
      <Script id="ga-init" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${GA_ID}', { send_page_view: false });
        `}
      </Script>
      <Suspense fallback={null}>
        <PageViewTracker />
      </Suspense>
    </>
  );
}
